import { useState, useMemo, useCallback } from 'react';

export interface PaginationResult<T> {
  items: T[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  setPage: (page: number) => void;
}

const usePagination = <T,>(data: T[], pageSize: number): PaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState(0);

  const totalCount = data.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginatedItems = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalCount);
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize, totalCount]);

  const setPage = useCallback((page: number) => {
    const validPage = Math.max(0, Math.min(page, totalPages - 1));
    setCurrentPage(validPage);
  }, [totalPages]);

  return {
    items: paginatedItems,
    currentPage,
    pageSize,
    totalPages,
    totalCount,
    setPage,
  };
}

export default usePagination;